import React, { useEffect, useState, Fragment, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, MessageSquare, X, Lock, Check, TrendingUp } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/7LBxnHifHEszohsnb2YYBf/AVY_LOGO.png';
const QR_CODE = 'https://cdn.magicpatterns.com/uploads/mty7WvpQictHZcsoU2UJsc/pasted-image.png';
const NATWEST_LOGO = 'https://cdn.magicpatterns.com/uploads/14JYWeFfSSgcPyYGWDWXnM/natwest-logo.png';
const WARWICK_LOGO = 'https://cdn.magicpatterns.com/uploads/nVjdVSHjwJfKFax4irgvhq/warwick.png';
const AVY_PROFILE = 'https://cdn.magicpatterns.com/uploads/dPc1daPUHuB5dUyGjajfUg/AvyPruthi_Profile_BW.png';
const NZ_IMAGE = 'https://cdn.magicpatterns.com/uploads/wAQphcBWWNHGHuMU1wzC5F/nz.jpg';
const EFFIE_AWARD = 'https://cdn.magicpatterns.com/uploads/1LHHJAx9EBeLeQe14j8iv3/effie.jpg';
const TRANSFORM_AWARDS = 'https://cdn.magicpatterns.com/uploads/hvEPBWtqhU7ruuhXDzLGjv/press-transform-awards.jpg';
const TRANSFORM_TEAM = 'https://cdn.magicpatterns.com/uploads/hhhuNAGTij4zSJyWsSXx2v/apruthi-grow-transform.jpg';
const PACQUIAO_DOHA = 'https://cdn.magicpatterns.com/uploads/kh3Tera9MPMWFYekxkFWyE/Manny_Pacquaio_arrives_to_Doha_0.jpg';
const LLAMA_PIC = 'https://cdn.magicpatterns.com/uploads/jz9awXzfiBokiBE6zJdx1b/PXL_20251228_061123752.jpg';
const MATT_PHOTO = 'https://cdn.magicpatterns.com/uploads/gJfMwR5Gsj1UP59px8fX41/matt-w.jpg';
const KIDZANIA_LOGO = 'https://cdn.magicpatterns.com/uploads/g9sK3Qh6s2YHDJ5UzYjV75/KidZania-Logo-1999.png';
const GMC_LOGO = 'https://cdn.magicpatterns.com/uploads/mzvA6wvseBLBU7THUyWcht/GMC-Logo.jpg';
const JLR_LOGO = 'https://cdn.magicpatterns.com/uploads/fFQTjrP6KZARRWKqH3rMLm/JLR_Logo_Border.png';
const AVERY_LOGO = 'https://cdn.magicpatterns.com/uploads/jYVqDHz5PxdQahXCHU8H55/averyweigh.png';
const AHLIBANK_LOGO = 'https://cdn.magicpatterns.com/uploads/tx83e22FBkrjUryASugdoa/ahlibank.png';
const OLYMPICS_LOGO = 'https://cdn.magicpatterns.com/uploads/rPc4Hms6pYeJjQSr8G8EVL/olympics.png';
const KIA_LOGO = 'https://cdn.magicpatterns.com/uploads/kYcGA91NqwDMhTmi42F88T/kia.webp';
const FULL_BIO_GRID = 'https://cdn.magicpatterns.com/uploads/7z2ZHSVyz6mescMtcwzbsC/avy-presentation-1920x1080.png';
const QATAR_OLYMPICS_LOGO = 'https://cdn.magicpatterns.com/uploads/rqB6Kpp1F4uQ3NxcY5Df8N/Qatar_Olympic_Committee_logo.svg.png';
const KIDZANIA_LOGO_ALT = 'https://cdn.magicpatterns.com/uploads/hogg68RLBXMj6X9ihUFeuT/KidZania-Logo-1999.png';
const AHLIBANK_LOGO_ALT = 'https://cdn.magicpatterns.com/uploads/9wYXnckAjhuhNs5dz3KkTF/ahlibank.png';
const JLR_LOGO_ALT = 'https://cdn.magicpatterns.com/uploads/vQtmfchRin1t6Zqbxem9ti/JLR_Logo_Border.png';
const KIA_LOGO_ALT = 'https://cdn.magicpatterns.com/uploads/xdmeH8JEkN3D2dTTfDLB3j/kia.webp';
const F1_VISUAL_NEW = 'https://cdn.magicpatterns.com/uploads/vpJ52rJZWLf8Ne4Z5UuhaS/Screenshot_2026-05-27_at_13.19.25.png';
const F1_VISUAL = 'https://cdn.magicpatterns.com/uploads/7LsdDJkJCazvsh62K3ku6a/f1.png';
const REVENUE_VISUAL = 'https://cdn.magicpatterns.com/uploads/2qivfs47o59ptZsaDxdku3/revenue.png';
const PRET_COFFEE = 'https://cdn.magicpatterns.com/uploads/bfpbjtu43PUqCNMCyWZJq3/pasted-image.jpg';
const CURRY_IMAGE = 'https://cdn.magicpatterns.com/uploads/vhTu1Tcwca3TApsuQBKvBt/Indian_Chicken_Curry_3.webp';
const ETIHAD_PLANE = 'https://cdn.magicpatterns.com/uploads/uLvWpwobQZZkGKxPWBnoKg/eithad.jpg';
interface SlideProps {
  isActive: boolean;
}
// --- Layout Wrapper for Slides ---
const SlideLayout = ({
  children,
  theme = 'light',
  showFooter = true,
  showLogo = true,
  footerTitle = 'Brand Clarity Workshop • May 2026'
}: {
  children: React.ReactNode;
  theme?: 'light' | 'dark' | 'yellow' | 'red' | 'navy' | 'green';
  showFooter?: boolean;
  showLogo?: boolean;
  footerTitle?: string;
}) => {
  const bgClass = {
    light: 'bg-[#fcfcfc] text-slate-900',
    dark: 'bg-[#0f172a] text-white',
    yellow: 'bg-yellow-400 text-black',
    red: 'bg-[#ff3b30] text-white',
    navy: 'bg-[#1a2b3c] text-white',
    green: 'bg-[#34c759] text-white'
  }[theme];
  return <div className={`relative h-full w-full flex flex-col px-8 md:px-24 py-16 md:py-20 overflow-hidden ${bgClass}`} data-id="element-0">
      {showLogo && <div className="absolute top-6 md:top-8 right-8 md:right-12 z-10" data-id="element-1">
          <img src={LOGO_URL} alt="AVY" className="w-8 h-8 md:w-10 md:h-10 object-contain" data-id="element-2" />
        </div>}

      <div className="flex-1 flex flex-col justify-center min-h-0" data-id="element-3">
        {children}
      </div>

      {showFooter && <div className="mt-8 md:mt-12 flex justify-between items-end text-[8px] md:text-[9px] uppercase tracking-[0.2em] font-medium opacity-30 flex-shrink-0" data-id="element-4">
          <div className="hidden md:block" data-id="element-5">AVY® | Confidential</div>
          <div className="text-center flex-1 md:flex-initial" data-id="element-6">
            {footerTitle}
          </div>
          <div className="hidden md:block text-right" data-id="element-7">www.avy.me</div>
        </div>}

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#ff3b30]" data-id="element-8" />
    </div>;
};
// --- Slide Components ---
const TitleSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="light" showLogo={false} footerTitle="How brand clarity maximises your funnel." data-id="element-9">
    <div className="flex flex-col md:flex-row items-center justify-between gap-12 h-full" data-id="element-10">
      <div className="flex-1 max-w-3xl" data-id="element-11">
        <motion.img src={LOGO_URL} className="w-20 h-20 mb-12" initial={{
        opacity: 0
      }} animate={isActive ? {
        opacity: 1
      } : {}} data-id="element-12" />
        <motion.h1 className="text-6xl md:text-[5.5rem] font-black leading-[1.1] tracking-tighter text-slate-900" initial={{
        opacity: 0,
        x: -50
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} data-id="element-13">
          How brand clarity maximises <span className="block" data-id="element-14">your</span>{' '}
          <span className="block" data-id="element-15">funnel.</span>
        </motion.h1>
        <div className="h-1 bg-[#ff3b30] w-32 my-12" data-id="element-16" />
        <motion.p className="text-2xl md:text-3xl font-medium text-slate-800" initial={{
        opacity: 0
      }} animate={isActive ? {
        opacity: 1
      } : {}} transition={{
        delay: 0.4
      }} data-id="element-17">
          Your funnel doesn't need bigger. It needs{' '}
          <span className="bg-yellow-400 px-2 py-1 text-black" data-id="element-18">better.</span>
        </motion.p>
      </div>

      <div className="flex flex-col items-center md:items-end gap-16" data-id="element-19">
        <motion.div className="flex flex-row md:flex-col gap-12 items-center md:items-end opacity-90" initial={{
        opacity: 0,
        x: 50
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: 0.5
      }} data-id="element-20">
          <img src={NATWEST_LOGO} alt="Natwest" className="h-10 md:h-14 object-contain" data-id="element-21" />
          <img src={WARWICK_LOGO} alt="Warwick" className="h-10 md:h-14 object-contain" data-id="element-22" />
        </motion.div>

        <motion.div className="relative group" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={isActive ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.7,
        type: 'spring'
      }} data-id="element-23">
          <div className="absolute -inset-4 bg-yellow-400/20 rounded-[2rem] blur-2xl group-hover:bg-yellow-400/30 transition-all" data-id="element-24" />
          <div className="relative p-6 bg-white shadow-2xl rounded-3xl border-2 border-slate-100" data-id="element-25">
            <img src={QR_CODE} alt="Connect on LinkedIn" className="w-32 md:w-40" data-id="element-26" />
            <div className="absolute -top-3 -right-3 bg-[#ff3b30] text-white text-[10px] font-black px-4 py-1.5 rounded-full shadow-xl rotate-12" data-id="element-27">
              LET'S CONNECT
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </SlideLayout>;
const BioSlide = ({
  isActive
}: SlideProps) => {
  return <SlideLayout theme="light" showLogo={false} data-id="element-28">
      <div className="relative w-full h-full flex items-center justify-center overflow-hidden" data-id="element-29">
        <motion.div className="w-full h-full flex items-center justify-center" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={isActive ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-30">
          <img src={FULL_BIO_GRID} className="w-full h-full object-contain" alt="Avy Pruthi - Experience and Partners" data-id="element-31" />
        </motion.div>
      </div>
    </SlideLayout>;
};
const StoryTemplateSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="light" data-id="element-32">
    <div className="flex flex-col items-center justify-center h-full text-center" data-id="element-33">
      <span className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-300 pb-1 mb-6" data-id="element-34">
        How I Got Here
      </span>
      <motion.h2 className="text-6xl md:text-9xl font-black tracking-tighter text-slate-900" initial={{
      opacity: 0,
      y: 20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-35">
        My Story
      </motion.h2>

      <motion.div className="absolute top-1/4 right-1/4 rotate-12 font-['Caveat',_cursive] text-3xl text-red-500 opacity-80" initial={{
      opacity: 0,
      scale: 0.5
    }} animate={isActive ? {
      opacity: 0.8,
      scale: 1
    } : {}} transition={{
      delay: 1
    }} data-id="element-36">
        Adam - Wall :-)
      </motion.div>
    </div>
  </SlideLayout>;
const WeakBrandingCostSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="dark" data-id="element-37">
    <div className="max-w-6xl" data-id="element-38">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400 border-b border-slate-700 pb-1" data-id="element-39">
        The Real Cost
      </span>
      <motion.h2 className="text-6xl md:text-8xl font-black mt-8 mb-16 tracking-tighter text-white" initial={{
      opacity: 0,
      y: 20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-40">
        What weak branding can cost you.
      </motion.h2>
      <div className="max-w-5xl space-y-6" data-id="element-41">
        {[{
        title: 'Leads that go cold',
        desc: 'Prospects visit, feel uncertain, and move on — without saying why'
      }, {
        title: 'Price objections on every call',
        desc: "When your brand doesn't do the pre-selling, you have to"
      }, {
        title: 'Slower growth',
        desc: "Word of mouth works less when people can't easily describe what you do"
      }, {
        title: 'Wrong-fit clients',
        desc: 'Vague brands attract vague enquiries — which waste time and energy'
      }].map((item, i) => <motion.div key={i} className="flex items-center gap-8 bg-slate-800/50 p-6 rounded-xl border border-white/5" initial={{
        opacity: 0,
        x: -20
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: i * 0.1
      }} data-id="element-42">
            <div className="w-1/3 text-yellow-500 font-black text-xl md:text-2xl" data-id="element-43">
              {item.title}
            </div>
            <div className="w-[2px] h-12 bg-slate-700" data-id="element-44" />
            <div className="flex-1 text-slate-300 text-lg" data-id="element-45">{item.desc}</div>
          </motion.div>)}
      </div>
    </div>
  </SlideLayout>;
const ExerciseSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="navy" data-id="element-46">
    <div className="flex items-center justify-between gap-12" data-id="element-47">
      <motion.h2 className="text-5xl md:text-8xl font-black tracking-tighter leading-tight max-w-5xl text-yellow-500" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={isActive ? {
      opacity: 1,
      scale: 1
    } : {}} data-id="element-48">
        What's your biggest branding challenge right now?
      </motion.h2>
      <motion.div className="text-9xl md:text-[12rem] bg-white rounded-2xl p-4" animate={isActive ? {
      rotate: [0, 15, 0, 15, 0]
    } : {}} transition={{
      repeat: Infinity,
      duration: 2
    }} data-id="element-49">
        👋
      </motion.div>
    </div>
  </SlideLayout>;
const CredentialsSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="dark" data-id="element-50">
    <motion.img src={LOGO_URL} className="w-20 h-20 mb-12" initial={{
    opacity: 0
  }} animate={isActive ? {
    opacity: 1
  } : {}} data-id="element-51" />
    <motion.h2 className="text-6xl md:text-9xl font-black tracking-tighter" initial={{
    opacity: 0,
    x: -50
  }} animate={isActive ? {
    opacity: 1,
    x: 0
  } : {}} data-id="element-52">
      Credentials
    </motion.h2>
    <div className="h-1 bg-[#ff3b30] w-48 mt-8" data-id="element-53" />
  </SlideLayout>;
const BrandIsNotSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="light" data-id="element-54">
    <motion.h2 className="text-4xl md:text-6xl font-black mb-12 tracking-tighter text-red-600" initial={{
    opacity: 0
  }} animate={isActive ? {
    opacity: 1
  } : {}} data-id="element-55">
      Brand is NOT.
    </motion.h2>
    <div className="max-w-3xl bg-red-50 p-12 rounded-[3rem] border-2 border-red-100" data-id="element-56">
      <ul className="space-y-8 text-2xl md:text-4xl font-black text-red-600" data-id="element-57">
        {['A logo', 'A colour palette', 'A font choice', 'A website redesign', 'A tagline'].map((item, i) => <motion.li key={i} className="flex items-center gap-6" initial={{
        opacity: 0,
        x: -20
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: i * 0.1
      }} data-id="element-58">
            <span className="text-4xl" data-id="element-59">✕</span> {item}
          </motion.li>)}
      </ul>
    </div>
  </SlideLayout>;
const BrandIsSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="green" data-id="element-60">
    <motion.h2 className="text-4xl md:text-6xl font-black mb-12 tracking-tighter text-white" initial={{
    opacity: 0
  }} animate={isActive ? {
    opacity: 1
  } : {}} data-id="element-61">
      Brand IS.
    </motion.h2>
    <div className="max-w-3xl bg-white p-12 rounded-[3rem] shadow-2xl" data-id="element-62">
      <ul className="space-y-8 text-2xl md:text-4xl font-black text-green-600" data-id="element-63">
        {[{
        label: 'Perception',
        desc: 'how they feel'
      }, {
        label: 'Trust',
        desc: 'the shortcut'
      }, {
        label: 'Positioning',
        desc: 'why you'
      }, {
        label: 'Promise',
        desc: 'the experience'
      }, {
        label: 'Voice',
        desc: 'how you sound'
      }].map((item, i) => <motion.li key={i} className="flex items-start gap-6" initial={{
        opacity: 0,
        x: 20
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: i * 0.1
      }} data-id="element-64">
            <span className="text-4xl mt-1" data-id="element-65">✓</span>
            <div data-id="element-66">
              {item.label}
              <span className="block text-slate-400 font-normal text-lg" data-id="element-67">
                — {item.desc}
              </span>
            </div>
          </motion.li>)}
      </ul>
    </div>
  </SlideLayout>;
const ProblemSlide = ({
  isActive,
  title = "What's Holding Brands Back?"
}: SlideProps & {
  title?: string;
}) => <SlideLayout theme="light" data-id="element-68">
    <div className="max-w-6xl" data-id="element-69">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400 border-b border-slate-200 pb-1" data-id="element-70">
        The Problem
      </span>
      <motion.h2 className="text-6xl md:text-8xl font-black mt-8 mb-16 tracking-tighter text-slate-900" initial={{
      opacity: 0,
      y: 20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-71">
        {title}
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-72">
        {[{
        title: 'Unclear positioning',
        desc: "Prospects can't tell what makes you different — so they go elsewhere"
      }, {
        title: 'DIY brand chaos',
        desc: "Pieced-together visuals and copy that don't connect or convert"
      }, {
        title: 'Undercharging',
        desc: 'Without brand trust, price becomes the only lever'
      }, {
        title: 'No perceived authority',
        desc: 'Hard to command premium prices without a brand that backs it up'
      }, {
        title: 'Inconsistent presence',
        desc: 'Mixed messages across platforms create confusion, not confidence'
      }].map((p, i) => <motion.div key={i} className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex items-start gap-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={isActive ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.2 + i * 0.1
      }} data-id="element-73">
            <div className="w-1.5 h-12 bg-red-500 shrink-0" data-id="element-74" />
            <div data-id="element-75">
              <h3 className="text-2xl font-black mb-2 text-slate-900" data-id="element-76">
                {p.title}
              </h3>
              <p className="text-slate-500 text-lg leading-relaxed" data-id="element-77">{p.desc}</p>
            </div>
          </motion.div>)}
      </div>
    </div>
  </SlideLayout>;
const WrapUpSlide = ({
  isActive
}: SlideProps) => <SlideLayout theme="yellow" data-id="element-78">
    <motion.h2 className="text-6xl md:text-9xl font-black tracking-tighter mb-16" initial={{
    opacity: 0,
    y: 50
  }} animate={isActive ? {
    opacity: 1,
    y: 0
  } : {}} data-id="element-79">
      Before we wrap up.
    </motion.h2>
    <div className="max-w-4xl space-y-8 text-xl md:text-2xl font-medium leading-relaxed" data-id="element-80">
      <motion.p initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} transition={{
      delay: 0.3
    }} data-id="element-81">
        "Brand clarity isn't magic. It's not a secret. It's not something only
        big companies with big budgets can access."
      </motion.p>
      <motion.p initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} transition={{
      delay: 0.5
    }} data-id="element-82">
        "It just takes being honest. About who you are. Who you're genuinely
        for. What you really do. What makes you actually different."
      </motion.p>
      <motion.p className="font-black text-3xl" initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} transition={{
      delay: 0.7
    }} data-id="element-83">
        "The more specific you are, the more magnetic you become to exactly the
        right people."
      </motion.p>
    </div>
  </SlideLayout>;
const SummarySlide = ({
  isActive
}: SlideProps) => {
  const items = [{
    area: 'Why brands fail to convert',
    insight: 'Weak brand clarity creates invisible, expensive leaks in your funnel'
  }, {
    area: 'What brand really is',
    insight: "Not a logo — it's perception, trust, positioning, promise, and voice. Pret gives away coffee to create loyalty worth thousands. Airlines remove row 13 because brand is a feeling. A restaurant in Benidorm fills 400 covers a day through service, not signage."
  }, {
    area: 'The brand–revenue link',
    insight: "A circular relationship: clarity → trust → better prices → referrals → stronger brand. Brand loyalty isn't a points card — it's a feeling. When you create it, the funnel feeds itself."
  }, {
    area: 'A · Align',
    insight: 'Why, who, what, how, where — get your story straight before anything else'
  }, {
    area: 'V · Visualise',
    insight: 'Express it visually and verbally, across every digital touchpoint — website, social, email, proposals. Clarity converts attention into opportunity.'
  }, {
    area: 'Y · Your Outcome',
    insight: 'When A and V are right, better clients, better prices, and real growth follow'
  }, {
    area: 'Positioning',
    insight: 'The live positioning exercise — your one-liner must be specific, clear, and valuable, in one sentence. This is how you attract the right clients.'
  }, {
    area: 'Examples',
    insight: 'HVAC landed their biggest contract. Avery stayed the global leader. Michelle became the influencer'
  }];
  return <SlideLayout theme="light" data-id="element-84">
      <motion.h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-slate-900" initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} data-id="element-85">
        What we covered today
      </motion.h2>
      <div className="space-y-2 overflow-y-auto pr-4 pb-4 max-h-[60vh]" data-id="element-86">
        <div className="flex text-[10px] uppercase tracking-widest font-bold text-[#ff3b30] mb-2" data-id="element-87">
          <div className="w-1/4" data-id="element-88">Area</div>
          <div className="flex-1" data-id="element-89">The key insight</div>
        </div>
        {items.map((item, i) => <motion.div key={i} className="flex py-3 border-t border-slate-100 items-start" initial={{
        opacity: 0,
        x: -20
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: 0.1 * i
      }} data-id="element-90">
            <div className="w-1/4 font-black text-slate-900 pr-4" data-id="element-91">
              {item.area}
            </div>
            <div className="flex-1 text-slate-600 text-sm leading-relaxed" data-id="element-92">
              {item.insight}
            </div>
          </motion.div>)}
      </div>
      <motion.div className="mt-6 p-6 bg-slate-50 rounded-xl border border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4" initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} transition={{
      delay: 1
    }} data-id="element-93">
        <div className="space-y-1" data-id="element-94">
          <p className="text-sm font-black text-slate-900" data-id="element-95">
            Gift: I'm here all day, also happy to do a brand clarity session,
            90mins session comp.
          </p>
          <p className="text-xs text-slate-500 font-medium" data-id="element-96">
            I have a small request — feedback.
          </p>
        </div>
        <a href="https://www.ws.avy.me/" className="text-[#ff3b30] font-black underline text-lg" data-id="element-97">
          https://www.ws.avy.me/
        </a>
      </motion.div>
    </SlideLayout>;
};
// --- Full 29 Slide Config ---
const SLIDE_CONFIG = [{
  id: 'title',
  component: TitleSlide,
  notes: "Welcome. Let's talk about brand clarity."
}, {
  id: 'discovery',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="yellow" data-id="element-98">
        <motion.h2 className="text-5xl md:text-8xl font-black mb-16 tracking-tighter leading-tight max-w-4xl" initial={{
      opacity: 0,
      scale: 0.95
    }} animate={isActive ? {
      opacity: 1,
      scale: 1
    } : {}} data-id="element-99">
          In the next 90'ish' mins, you'll discover.
        </motion.h2>
        <div className="space-y-8" data-id="element-100">
          {['Exactly how branding drives real revenue', 'What brand clarity actually means', 'A three-part framework you can start using', 'Real client stories', 'A free gift for everyone in this room'].map((item, i) => <motion.div key={i} className="flex items-start gap-6 text-xl md:text-2xl font-bold max-w-3xl" initial={{
        opacity: 0,
        y: 20
      }} animate={isActive ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1 * i + 0.3
      }} data-id="element-101">
              <span className="w-8 h-[2px] bg-black mt-4 shrink-0" data-id="element-102"></span>
              {item}
            </motion.div>)}
        </div>
        <motion.div className="absolute bottom-12 right-12 text-4xl opacity-40" initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 0.4
    } : {}} data-id="element-103">
          🙏
        </motion.div>
      </SlideLayout>,
  notes: "What you're walking away with."
}, {
  id: 'agenda',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-104">
        <motion.h2 className="text-5xl md:text-7xl font-black mb-16 tracking-tighter" initial={{
      opacity: 0,
      y: -20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-105">
          Today's agenda.
        </motion.h2>
        <div className="space-y-6" data-id="element-106">
          {['The problem', 'What branding actually is', 'The brand-revenue link', 'A 3-part framework', 'How to position yourself', 'Real client stories', 'Your next steps'].map((item, i) => <motion.div key={i} className="flex items-center gap-6 text-xl md:text-2xl font-bold" initial={{
        opacity: 0,
        x: -20
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        delay: 0.1 * i + 0.3
      }} data-id="element-107">
              <span className="w-8 h-[2px] bg-[#ff3b30]" data-id="element-108"></span>
              <span className="text-gray-400 mr-2" data-id="element-109">{i + 1} ·</span>
              {item}
            </motion.div>)}
        </div>
      </SlideLayout>,
  notes: 'Seven areas. All connected.'
}, {
  id: 'bio',
  component: BioSlide,
  notes: 'Background and motivation.'
}, {
  id: 'deal',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-110">
        <motion.h2 className="text-3xl font-bold text-gray-400 uppercase tracking-widest mb-12" initial={{
      opacity: 0
    }} animate={isActive ? {
      opacity: 1
    } : {}} data-id="element-111">
          Deal?
        </motion.h2>
        <div className="max-w-5xl space-y-12 text-2xl md:text-4xl font-medium leading-tight text-gray-800" data-id="element-112">
          <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={isActive ? {
        opacity: 1,
        y: 0
      } : {}} data-id="element-113">
            In the next 90 mins — i'll share a series of short stories which
            will help you think about branding for your business most
            importantly, how it increases revenue.
          </motion.p>
          <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={isActive ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2
      }} data-id="element-114">
            During the session we'll do a few activities. As cheesy as this
            sounds, for you to leave with value, i'd like you to be{' '}
            <span className="underline decoration-yellow-400 decoration-4 underline-offset-8" data-id="element-115">
              open, honest and transparent
            </span>{' '}
            about where you are with your business.
          </motion.p>
          <motion.p className="text-5xl md:text-7xl font-black text-black pt-8" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={isActive ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.4
      }} data-id="element-116">
            Ok, lets do it.
          </motion.p>
        </div>
      </SlideLayout>,
  notes: 'Setting the stage.'
}, {
  id: 'problem-1',
  component: (props: SlideProps) => <ProblemSlide {...props} title="What's holding brands back?" data-id="element-117" />,
  notes: 'The core problem.'
}, {
  id: 'question',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-118">
        <div className="max-w-5xl space-y-12 relative" data-id="element-119">
          <motion.h2 className="text-4xl md:text-6xl font-black tracking-tighter text-slate-300" initial={{
        opacity: 0
      }} animate={isActive ? {
        opacity: 1
      } : {}} data-id="element-120">
            Before I tell you what brand is...
          </motion.h2>
          <div className="space-y-8" data-id="element-121">
            {['Who here has ever watched a less experienced competitor win the deal you should have landed?', "How many of you have been told you're 'too expensive' even when you know your price is completely fair?", 'Has anyone had a prospect go from engaged and excited to radio-silent, with no clear reason why?'].map((q, i) => <motion.p key={i} className="text-2xl md:text-4xl font-bold text-slate-900 leading-tight" initial={{
          opacity: 0,
          x: -30
        }} animate={isActive ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          delay: 0.3 + i * 0.2
        }} data-id="element-122">
                {q}
              </motion.p>)}
          </div>
          <motion.div className="absolute -top-12 right-0 text-6xl" initial={{
        opacity: 0,
        scale: 0
      }} animate={isActive ? {
        opacity: 1,
        scale: 1,
        rotate: [0, 15, -10, 15, 0]
      } : {}} transition={{
        delay: 1,
        duration: 1
      }} data-id="element-123">
            👋
          </motion.div>
        </div>
      </SlideLayout>,
  notes: 'Interactive questions.'
}, {
  id: 'brand-is-not',
  component: BrandIsNotSlide,
  notes: 'What branding is NOT.'
}, {
  id: 'brand-is',
  component: BrandIsSlide,
  notes: 'What branding IS.'
}, {
  id: 'problem-2',
  component: WeakBrandingCostSlide,
  notes: 'The real cost of weak branding.'
}, {
  id: 'exercise',
  component: ExerciseSlide,
  notes: 'Roundtable discussion.'
}, {
  id: 'realization',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-124">
        <div className="relative h-full flex flex-col justify-center" data-id="element-125">
          <motion.h2 className="text-5xl md:text-7xl font-black mb-12 tracking-tighter" initial={{
        opacity: 0,
        x: -30
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} data-id="element-126">
            But here's what I realised.
          </motion.h2>
          <div className="max-w-4xl space-y-8 text-xl md:text-2xl text-gray-700 leading-relaxed" data-id="element-127">
            <p data-id="element-128">
              The founders who needed it most had no access to any of it. It was
              locked behind agencies and big retainers.
            </p>
            <p className="text-3xl font-black text-black" data-id="element-129">
              It just didn't sit right with me.
            </p>
          </div>
        </div>
      </SlideLayout>,
  notes: 'The gap in the market.'
}, {
  id: 'credibility',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-130">
        <motion.h2 className="text-5xl md:text-7xl font-black mb-12 tracking-tighter" initial={{
      opacity: 0,
      y: -20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-131">
          Here's how I know this.
        </motion.h2>
        <div className="max-w-5xl space-y-12" data-id="element-132">
          <div className="flex flex-col md:flex-row items-baseline gap-8" data-id="element-133">
            <motion.p className="text-2xl md:text-4xl font-bold text-slate-900 leading-tight" initial={{
          opacity: 0
        }} animate={isActive ? {
          opacity: 1
        } : {}} data-id="element-134">
              <span className="text-6xl md:text-9xl font-black block mb-4" data-id="element-135">
                I
              </span>
              spent 15 years working on brand projects for a number of
              recognised organisations
            </motion.p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 items-center pt-8" data-id="element-136">
            {[QATAR_OLYMPICS_LOGO, KIDZANIA_LOGO_ALT, AHLIBANK_LOGO_ALT, JLR_LOGO_ALT, KIA_LOGO_ALT].map((logo, i) => <motion.div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center h-32 md:h-40" initial={{
          opacity: 0,
          y: 20
        }} animate={isActive ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.2 + i * 0.1
        }} data-id="element-137">
                <img src={logo} className="max-h-full max-w-full object-contain" alt="Partner Logo" data-id="element-138" />
              </motion.div>)}
          </div>
        </div>
      </SlideLayout>,
  notes: 'Experience at scale.'
}, {
  id: 'decision',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-139">
        <motion.h2 className="text-5xl md:text-7xl font-black mb-12 tracking-tighter" initial={{
      opacity: 0,
      y: -20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-140">
          So I made a decision.
        </motion.h2>
        <div className="max-w-4xl space-y-8 text-xl md:text-2xl text-gray-700 leading-relaxed" data-id="element-141">
          <p data-id="element-142">
            I decided to take everything I learned from the big guys and give it
            to the people who actually move the needle.
          </p>
        </div>
      </SlideLayout>,
  notes: 'So I made a decision.'
}, {
  id: 'proof',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-143">
        <motion.h2 className="text-5xl md:text-7xl font-black mb-12 tracking-tighter" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={isActive ? {
      opacity: 1,
      scale: 1
    } : {}} data-id="element-144">
          My first client proved it.
        </motion.h2>
        <div className="max-w-4xl space-y-8 text-xl md:text-2xl text-gray-700 leading-relaxed" data-id="element-145">
          <p data-id="element-146">
            They were doing everything right, but their brand was holding them
            back. Within 3 months of clarity, their revenue doubled.
          </p>
        </div>
      </SlideLayout>,
  notes: 'My first client proved it.'
}, {
  id: 'definition',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="yellow" showLogo={false} data-id="element-147">
        <motion.h2 className="text-6xl md:text-9xl font-black mt-4 tracking-tighter" initial={{
      opacity: 0,
      y: 20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-148">
          What branding <span className="block text-white" data-id="element-149">actually is.</span>
        </motion.h2>
      </SlideLayout>,
  notes: 'Transition to definition.'
}, {
  id: 'pret',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-150">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 h-full items-center" data-id="element-151">
          {[{
        src: PRET_COFFEE,
        alt: 'Pret'
      }, {
        src: CURRY_IMAGE,
        alt: 'Curry'
      }, {
        src: ETIHAD_PLANE,
        alt: 'Etihad'
      }].map((img, i) => <motion.div key={i} className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white h-64 md:h-80" initial={{
        opacity: 0,
        scale: 0.8,
        y: 20
      }} animate={isActive ? {
        opacity: 1,
        scale: 1,
        y: 0
      } : {}} transition={{
        delay: i * 0.2
      }} data-id="element-152">
              <img src={img.src} className="w-full h-full object-cover" alt={img.alt} data-id="element-153" />
            </motion.div>)}
        </div>
      </SlideLayout>,
  notes: 'Visual branding examples.'
}, {
  id: 'case-intro',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="yellow" data-id="element-154">
        <motion.h2 className="text-6xl md:text-9xl font-black tracking-tighter leading-none max-w-5xl mb-12" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={isActive ? {
      opacity: 1,
      scale: 1
    } : {}} data-id="element-155">
          Let me show you exactly what this looks like.
        </motion.h2>
      </SlideLayout>,
  notes: 'Transition to case studies.'
}, {
  id: 'hvac-prob',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-156">
        <motion.h2 className="text-6xl md:text-9xl font-black mt-4 tracking-tighter" initial={{
      opacity: 0,
      x: -50
    }} animate={isActive ? {
      opacity: 1,
      x: 0
    } : {}} data-id="element-157">
          The HVAC company.
        </motion.h2>
        <p className="text-red-500 font-bold text-3xl mt-12" data-id="element-158">
          "They pitched. They lost."
        </p>
      </SlideLayout>,
  notes: 'HVAC problem.'
}, {
  id: 'hvac-fix',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-159">
        <motion.h2 className="text-6xl md:text-9xl font-black mt-4 tracking-tighter" initial={{
      opacity: 0,
      y: 20
    }} animate={isActive ? {
      opacity: 1,
      y: 0
    } : {}} data-id="element-160">
          What we changed.
        </motion.h2>
        <p className="text-3xl font-black text-yellow-500 mt-12" data-id="element-161">
          Same company. Same skills. Different look.
        </p>
      </SlideLayout>,
  notes: 'HVAC solution.'
}, {
  id: 'hvac-res',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="dark" showLogo={false} data-id="element-162">
        <div className="flex flex-col md:flex-row h-full rounded-[2rem] overflow-hidden shadow-2xl border border-white/10" data-id="element-163">
          <div className="flex-1 relative" data-id="element-164">
            <img src={F1_VISUAL_NEW} className="w-full h-full object-cover" alt="F1" data-id="element-165" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" data-id="element-166" />
          </div>
          <div className="flex-1 bg-[#0a1120] p-12 flex flex-col justify-center" data-id="element-167">
            <span className="text-blue-400 font-black uppercase tracking-widest text-xs mb-8" data-id="element-168">
              What our clients say
            </span>
            <p className="text-2xl md:text-4xl font-medium leading-tight mb-12" data-id="element-169">
              "In our industry you feel every one percent. Getting air quality
              and temperature right in our offices has been a game-changer for
              focus and performance."
            </p>
            <div className="flex items-center gap-4" data-id="element-170">
              <div className="w-12 h-[2px] bg-blue-500" data-id="element-171" />
              <div data-id="element-172">
                <div className="font-black text-white" data-id="element-173">CEO</div>
                <div className="text-slate-500 text-sm" data-id="element-174">
                  Automotive Industry
                </div>
              </div>
            </div>
          </div>
        </div>
      </SlideLayout>,
  notes: 'HVAC outcome.'
}, {
  id: 'global-case',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-175">
        <div className="flex flex-col justify-center h-full max-w-5xl" data-id="element-176">
          <motion.h2 className="text-5xl md:text-8xl font-black tracking-tighter leading-none mb-8" initial={{
        opacity: 0,
        x: -50
      }} animate={isActive ? {
        opacity: 1,
        x: 0
      } : {}} data-id="element-177">
            Avery Weigh Tronix.
          </motion.h2>
          <p className="text-2xl font-bold text-gray-400 mb-12" data-id="element-178">
            Birmingham. Fortune 250. UK and Global.
          </p>
          <motion.div className="bg-slate-50 p-10 rounded-3xl border border-slate-100 italic text-2xl md:text-3xl text-slate-700 leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} animate={isActive ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.3
      }} data-id="element-179">
            "We're global, in a mature industry. Our brand looked tired. Avy
            showed us how to look like the innovator we actually are. We stayed
            the leader. We stayed relevant."
            <div className="mt-8 text-sm font-black uppercase tracking-widest text-slate-400" data-id="element-180">
              — Avery Weigh Tronix, Global company
            </div>
          </motion.div>
        </div>
      </SlideLayout>,
  notes: 'Global case study.'
}, {
  id: 'revenue',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-181">
        <div className="flex flex-col items-center justify-center h-full gap-16" data-id="element-182">
          <motion.h2 className="text-4xl md:text-6xl font-black tracking-tighter text-slate-900" initial={{
        opacity: 0
      }} animate={isActive ? {
        opacity: 1
      } : {}} data-id="element-183">
            The brand-revenue chain
          </motion.h2>

          <div className="flex flex-wrap justify-center items-center gap-4 max-w-6xl" data-id="element-184">
            {[{
          label: 'Brand Clarity',
          desc: 'People immediately understand who you are'
        }, {
          label: 'Trust',
          desc: 'Prospects feel safe choosing you'
        }, {
          label: 'Higher Prices',
          desc: 'You stop competing on cost'
        }, {
          label: 'Referrals',
          desc: 'Clients describe you clearly to others'
        }, {
          label: 'Sustainable Scale',
          desc: 'Each client brings the next'
        }].map((item, i) => <Fragment key={i} data-id="element-185">
                <motion.div className="w-48 md:w-56 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center space-y-3" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={isActive ? {
            opacity: 1,
            scale: 1
          } : {}} transition={{
            delay: i * 0.1
          }} data-id="element-186">
                  <div className="text-[#ff3b30] font-black text-lg leading-tight" data-id="element-187">
                    {item.label}
                  </div>
                  <div className="text-slate-500 text-xs leading-relaxed" data-id="element-188">
                    {item.desc}
                  </div>
                </motion.div>
                {i < 4 && <div className="w-4 h-[2px] bg-slate-200 hidden md:block" data-id="element-189" />}
              </Fragment>)}
          </div>
        </div>
      </SlideLayout>,
  notes: 'Revenue model.'
}, {
  id: 'framework',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-190">
        <div className="flex flex-col items-center justify-center h-full relative" data-id="element-191">
          <motion.h2 className="text-6xl md:text-9xl font-black mb-16 tracking-tighter" initial={{
        opacity: 0,
        y: -20
      }} animate={isActive ? {
        opacity: 1,
        y: 0
      } : {}} data-id="element-192">
            The A · V · Y framework.
          </motion.h2>

          <div className="relative w-80 h-80 md:w-[500px] md:h-[500px] flex items-center justify-center" data-id="element-193">
            {/* Triangle Base */}
            <div className="absolute inset-0 border-[10px] border-slate-900 rounded-full opacity-5" data-id="element-194" />

            {/* Icons */}
            <motion.div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white p-6 rounded-full shadow-xl z-20" initial={{
          opacity: 0,
          y: -20
        }} animate={isActive ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.3
        }} data-id="element-195">
              <Lock size={40} data-id="element-196" />
            </motion.div>

            <motion.div className="absolute top-1/2 -right-10 -translate-y-1/2 bg-green-500 text-white p-6 rounded-full shadow-xl z-20" initial={{
          opacity: 0,
          x: 20
        }} animate={isActive ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          delay: 0.5
        }} data-id="element-197">
              <Check size={40} data-id="element-198" />
            </motion.div>

            <motion.div className="absolute top-1/2 -left-10 -translate-y-1/2 bg-yellow-400 text-black p-6 rounded-full shadow-xl z-20" initial={{
          opacity: 0,
          x: -20
        }} animate={isActive ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          delay: 0.7
        }} data-id="element-199">
              <TrendingUp size={40} data-id="element-200" />
            </motion.div>

            <div className="text-center space-y-4" data-id="element-201">
              <div className="text-8xl md:text-[12rem] font-black tracking-tighter leading-none" data-id="element-202">
                AVY
              </div>
            </div>
          </div>
        </div>
      </SlideLayout>,
  notes: 'The core system.'
}, {
  id: 'align',
  component: ({
    isActive
  }: SlideProps) => <SlideLayout theme="light" data-id="element-203">
        <motion.h2 className="text-4xl md:text-6xl font-black mb-16 tracking-tighter" initial={{
      opacity: 0,
      x: -30
    }} animate={isActive ? {
      opacity: 1,
      x: 0
    } : {}} data-id="element-204">
          Within Align, you need to be clear on:
        </motion.h2>
        <ul className="space-y-4 text-2xl font-bold" data-id="element-205">
          {['Why', 'Who', 'What', 'How', 'Where'].map((p, i) => <li key={i} data-id="element-206">{p}</li>)}
        </ul>
      </SlideLayout>,
  notes: 'Align phase.'
}, {
  id: 'wrapup',
  component: WrapUpSlide,
  notes: "Clarity isn't magic. It's honesty."
}, {
  id: 'summary',
  component: SummarySlide,
  notes: 'Recap of the A·V·Y system.'
}];
export function App() {
  const screenInit = useScreenInit();
  const [currentSlide, setCurrentSlide] = useState(screenInit?.currentSlide ?? 0);
  const [showNotes, setShowNotes] = useState(false);
  useEffect(() => {
    if (screenInit?.currentSlide !== undefined) {
      setCurrentSlide(screenInit.currentSlide);
    }
  }, [screenInit]);
  const nextSlide = () => setCurrentSlide(prev => Math.min(prev + 1, SLIDE_CONFIG.length - 1));
  const prevSlide = () => setCurrentSlide(prev => Math.max(prev - 1, 0));
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'n') setShowNotes(!showNotes);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showNotes]);
  const slide = SLIDE_CONFIG[currentSlide];
  return <div className="slide-container bg-black overflow-hidden select-none" data-id="element-207">
      <div className="relative flex-1 flex overflow-hidden" data-id="element-208">
        <AnimatePresence mode="wait" data-id="element-209">
          <motion.div key={currentSlide} initial={{
          opacity: 0,
          x: 100
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="w-full h-full" data-id="element-210">
            {createElement(slide.component, {
            isActive: true
          })}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence data-id="element-211">
          {showNotes && <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} className="absolute right-0 top-0 bottom-0 w-80 md:w-96 bg-gray-900 text-white p-8 z-50 border-l border-gray-800" data-id="element-212">
              <div className="flex justify-between items-center mb-8" data-id="element-213">
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400" data-id="element-214">
                  Speaker Notes
                </h3>
                <button onClick={() => setShowNotes(false)} className="p-1 hover:bg-gray-800 rounded" data-id="element-215">
                  <X size={20} data-id="element-216" />
                </button>
              </div>
              <p className="text-lg leading-relaxed font-medium text-gray-300 italic" data-id="element-217">
                "{slide.notes}"
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 right-6 flex items-center gap-1.5 z-[100]" data-id="element-218">
        <button onClick={prevSlide} className="p-1.5 bg-white/60 backdrop-blur hover:bg-white text-slate-400 hover:text-slate-900 rounded-full shadow-sm transition-all active:scale-90 border border-slate-100" title="Previous (ArrowLeft)" data-id="element-219">
          <ChevronLeft size={14} data-id="element-220" />
        </button>
        <div className="px-2 py-1 text-[10px] text-slate-400 font-medium bg-white/40 backdrop-blur rounded-md border border-slate-100/50" data-id="element-221">
          {currentSlide + 1}
        </div>
        <button onClick={nextSlide} className="p-1.5 bg-slate-900/10 backdrop-blur hover:bg-slate-900 text-slate-600 hover:text-white rounded-full shadow-sm transition-all active:scale-90" title="Next (ArrowRight / Space)" data-id="element-222">
          <ChevronRight size={14} data-id="element-223" />
        </button>
        <button onClick={() => setShowNotes(!showNotes)} className={`p-1.5 rounded-full shadow-sm transition-all active:scale-90 ${showNotes ? 'bg-yellow-400 text-black' : 'bg-white/60 backdrop-blur text-slate-400 border border-slate-100'}`} title="Speaker Notes (N)" data-id="element-224">
          <MessageSquare size={14} data-id="element-225" />
        </button>
      </div>
    </div>;
}